/**
 * "Copy Text (detailed)": the office's PLAN INFO sheet as plain text.
 *
 * Same labels and order as the yellow paper form, one procedure family per
 * entry, blanks (`___`) wherever the carrier told us nothing — so a pasted
 * sheet always has the same shape and staff can fill the gaps by hand.
 */
import dayjs from "dayjs";

import { BLANK, NO_MAX_STATED } from "./exportModel";
import { alternateBenefitWords, describeSharedLimit } from "../../../lib/planDetails";

const SEP = "   ";

function orBlank(v) {
  return v == null || v === "" ? BLANK : String(v);
}

// The office reads "Employer / Plan" as the group (contract) number, with the
// carrier's plan name after it when we have one: "078801 — SR-SUN LIFE".
function employerPlan(plan) {
  if (plan.groupNumber) return `${plan.groupNumber}${plan.planName ? ` — ${plan.planName}` : ""}`;
  return orBlank(plan.planName);
}

// "2026 Ontario (Current)" / "2025 Ontario (1 yr behind)"; a label with no year
// already says what it is ("One-year lag fee guide") and prints verbatim.
function feeGuideLine(plan) {
  if (!plan.feeGuide) return BLANK;
  if (!/(19|20)\d{2}/.test(plan.feeGuide)) return plan.feeGuide;
  if (plan.feeGuideToken === "C") return /current/i.test(plan.feeGuide) ? plan.feeGuide : `${plan.feeGuide} (Current)`;
  const m = /^-(\d+)$/.exec(plan.feeGuideToken || "");
  return m ? `${plan.feeGuide} (${m[1]} yr behind)` : plan.feeGuide;
}

// Only a computed shared-maximum group (or the plan note) can say "combined";
// when the pools were worked out and basic and major sit in different ones the
// answer is N. Two maxima that merely look alike (an FTC typing 2000 and 2000)
// prove nothing either way and stay blank.
function combinedLine(maxima) {
  if (maxima.combined === true) return "Y";
  if (maxima.combined === false) return "N";
  return BLANK;
}

// "$2,500.00 (remaining $2,500.00)", "N" for a category the plan does not
// cover (0%, said not covered, or a $0 maximum), "no max stated" when the
// carrier quoted a percentage but no dollar cap, blank when nothing is known.
function withRemaining(m) {
  if (m.off || m.max === "$0.00") return "N";
  if (m.max === BLANK) return BLANK;
  if (m.noMaxStated) return NO_MAX_STATED;
  if (m.fromNote) return `${m.max} (per plan note)`;
  if (m.max === "Unlimited") return "Unlimited";
  return `${m.max} (remaining ${m.remaining})`;
}

// "not yet checked" is a portal state; a breakdown keyed by a person has no
// per-code eligibility, so the column is left out rather than filled with it.
function availabilityCol(fam, manual) {
  return manual ? "" : `${SEP}${availability(fam)}`;
}

// "Recall 9M R", or the age split the carrier spelled out: "Recall 6M under 19 / 9M 19+".
function recallToken(fam) {
  if (fam.recallSplit && fam.recallSplit.length) {
    return fam.recallSplit.map((s) => `${s.months}M ${s.who}`).join(" / ");
  }
  return fam.frequency === BLANK ? null : paperFreq(fam.frequency);
}

// "12MR" → "12M R": the paper form writes the rolling flag as its own letter.
function paperFreq(token) {
  return token === BLANK ? BLANK : token.replace(/MR$/, "M R");
}

// "Recall 12M R (eligible Apr 1, 2027)" — the sheet's Examination Limitations.
function examLimitation(fam, name) {
  if (!fam || !fam.row || fam.unavailable) return null;
  const freq = fam.key === "recall" ? recallToken(fam) : fam.frequency === BLANK ? null : paperFreq(fam.frequency);
  const when = fam.state === "eligible" ? "eligible today" : fam.eligibleAgain ? `eligible ${fam.eligibleAgain}` : null;
  // "Emergency exam 1/12M (shared with 01204 · 2 every 12 months combined)".
  const shared = describeSharedLimit(fam.row);
  // A line the carrier answered with a status rather than a limit says so.
  if (!freq && !when && !shared && fam.status) return `${name}: ${fam.status.toLowerCase()}`;
  if (!freq && !when && !shared) return null; // nothing known: say nothing rather than name a blank
  const parts = [name, freq].filter(Boolean).join(" ");
  const notes = [when, shared].filter(Boolean).join("; ");
  return notes ? `${parts} (${notes})` : parts;
}

// "Y — paid as an alternate": white fillings on molars the carrier pays at the
// silver rate. Cleer's "white fillings on molars allowed?" is this line.
function molarCompositesLine(fam) {
  const alt = fam.row ? alternateBenefitWords(fam.row.alternate_benefit) : "";
  return alt && fam.yesNo !== BLANK ? `${fam.yesNo} — ${alt.toLowerCase()}` : fam.yesNo;
}

function availability(fam) {
  if (!fam || !fam.row || fam.unavailable) return BLANK;
  if (fam.state === "eligible") return "eligible today";
  if (fam.eligibleAgain) return `next ${fam.eligibleAgain}`;
  if (fam.state === "not_covered" || fam.state === "not_found") return "not covered";
  return fam.stateLabel ? fam.stateLabel.toLowerCase() : BLANK;
}

function freqText(fam) {
  return fam ? paperFreq(fam.frequency) : BLANK;
}

function pctLine(fam) {
  if (!fam) return BLANK;
  if (fam.pct === "N") return "N — not covered";
  return fam.pct;
}

// " (27211)" — the code the carrier was asked about. When it is not the paper
// form's own code (ProviderConnect asks 27201, Sun Life asks the partial
// denture 53201) it is named, so "Denture (53201 partial denture, cast,
// upper): 50%" says exactly what was answered; an unanswered line keeps the
// form's code for the office to fill in by hand.
function withCode(fam) {
  if (!fam) return "";
  if (!fam.row || !fam.code) return fam.templateCode ? ` (${fam.templateCode})` : "";
  if (fam.code === fam.templateCode || !fam.answeredName) return ` (${fam.code})`;
  return ` (${fam.code} ${fam.answeredName})`;
}

// "80%, 8 units / 12 consecutive months" for root planing — the % and the
// allowance, or a single blank.
function pctAndUnits(fam) {
  const pct = pctLine(fam);
  const units = fam && fam.row && !fam.unavailable ? fam.scalingUnits : BLANK;
  if (pct === BLANK && units === BLANK) return BLANK;
  return units === BLANK ? pct : `${pct}, ${units}`;
}

// "Basic" / "Major" qualifies a known percentage; a blank needs no qualifier.
function basicOrMajor(fam) {
  return fam && fam.basicOrMajor && fam.pct !== BLANK ? ` ${fam.basicOrMajor}` : "";
}

// "80%, 9M R" — or a single blank when neither the % nor the frequency is known.
function pctAndFreq(fam) {
  const pct = pctLine(fam);
  const freq = freqText(fam);
  if (pct === BLANK && freq === BLANK) return BLANK;
  return `${pct}, ${freq}`;
}

// A recorded answer as the sheet's token: Yes → Y, No → N, anything else as said.
function answerToken(words) {
  if (!words) return BLANK;
  return words === "Yes" ? "Y" : words === "No" ? "N" : words;
}

export function renderPlanInfoText(model) {
  const { patient, member, carrier, plan, maxima, family, exceptions, unavailable, provenance, planNote } = model;
  const f = family;
  const d = plan.details || {};
  const manual = !!(provenance && provenance.isManual);
  const lines = [];

  lines.push(`PLAN INFO — ${patient.name}${patient.dob ? ` (DOB ${patient.dob})` : ""}`);
  if (planNote && planNote.active === false) lines.push("PLAN STATUS: NOT ACTIVE (per plan note)");
  lines.push(
    `Carrier / Phone #: ${orBlank(carrier.name)} / ${orBlank(carrier.phone)}` +
    `${SEP}Portal: ${orBlank(carrier.portal)}`,
  );
  const memberText = member.name
    ? `${member.name}${member.relation ? ` (${member.relation})` : ""}`
    : BLANK;
  lines.push(`Employer / Plan: ${employerPlan(plan)}${SEP}Member: ${memberText}`);
  lines.push(`Policy #: ${orBlank(plan.groupNumber)}${SEP}Certificate #: ${orBlank(plan.memberId)}${SEP}Account #: ${BLANK}`);
  const spScope = d.specialist_fee_scope ? ` (${d.specialist_fee_scope})` : "";
  lines.push(
    `Fee Guide: ${feeGuideLine(plan)}${SEP}Sp Fee: ${plan.specialistFee.token}${spScope}` +
    `${SEP}Assignment: ${answerToken(d.assignment_of_benefits)}${SEP}EDI: ${BLANK}`,
  );
  lines.push(
    `Maximum: Basic ${withRemaining(maxima.basic)}, Major ${withRemaining(maxima.major)}, Ortho ${withRemaining(maxima.ortho)}` +
    `${SEP}Combined: ${combinedLine(maxima)}` +
    (d.max_scope ? `${SEP}Applies: ${d.max_scope.toLowerCase()}` : ""),
  );
  lines.push(`Deductible: ${orBlank(plan.deductible)}${d.coverage_scope ? `${SEP}Coverage: ${d.coverage_scope}` : ""}`);
  const yearType = d.benefit_period_type && d.benefit_period_type !== "Unknown"
    ? ` (${d.benefit_period_type.toLowerCase()})` : "";
  lines.push(
    `Plan Renews: ${orBlank(plan.renews)}${yearType}` +
    (d.coverage_end_date ? `${SEP}Coverage Ends: ${d.coverage_end_date}` : "") +
    `${SEP}Dep. Child covered to age: ${orBlank(plan.depChildAge)}` +
    `${SEP}FT Students covered to age: ${orBlank(plan.studentAge)}` +
    (plan.memberAge ? `${SEP}Plan age limit: ${plan.memberAge}` : ""),
  );
  // The clauses a breakdown must answer: printed blank until someone asks.
  const altNote = d.alternate_benefit_note ? ` (${d.alternate_benefit_note})` : "";
  const implantPct = d.implants_percentage ? ` ${d.implants_percentage}` : "";
  lines.push(
    `Missing Tooth Clause: ${answerToken(d.missing_tooth_clause)}` +
    `${SEP}Alternate Benefit: ${answerToken(d.alternate_benefit)}${altNote}` +
    `${SEP}Implants: ${d.implants_coverage ? `${d.implants_coverage}${implantPct}` : BLANK}` +
    (d.predetermination_threshold ? `${SEP}Predetermination over: ${d.predetermination_threshold}` : ""),
  );
  if (d.waiting_period_note) lines.push(`Waiting Periods: ${d.waiting_period_note}`);
  lines.push(`Nitrous Covered (92413): ${f.nitrous.yesNo}`);
  const exams = [
    examLimitation(f.recall, "Recall"), examLimitation(f.exam, "Complete exam"),
    // Their own limits, not the recall's — printed when the carrier was asked.
    examLimitation(f.emergencyExam, "Emergency exam"), examLimitation(f.specificExam, "Specific exam"),
    examLimitation(f.newPatientExam, "New-patient exam"),
  ].filter(Boolean);
  lines.push(`Examination Limitations: ${exams.length ? exams.join("; ") : BLANK}`);
  const limitationsRefs = [planNote ? "plan note" : null, exceptions.length ? "exceptions" : null].filter(Boolean);
  lines.push(`Limitations: ${limitationsRefs.length ? `see ${limitationsRefs.join(" and ")} below` : BLANK}`);

  if (planNote) {
    lines.push("");
    lines.push(`PLAN NOTE${planNote.date ? ` (${planNote.date})` : ""}`);
    planNote.lines.forEach((l) => lines.push(`- ${l}`));
  }

  lines.push("");
  lines.push(manual ? "RECARE / HYGIENE" : "RECARE / HYGIENE — what's available today");
  const cycle = f.recall.recallSplit && f.recall.recallSplit.length
    ? f.recall.recallSplit.map((sp) => `${sp.months}M ${sp.who}`).join(" / ")
    : f.recall.calendar && f.recall.row && Number(f.recall.row.max_occurrences) > 1
      ? `${f.recall.row.max_occurrences} per year${f.recall.minGapMonths ? ` (${f.recall.minGapMonths} mo apart)` : ""}`
      : f.recall.cycleMonths ? `${f.recall.cycleMonths}M${/R$/.test(f.recall.frequency) ? " R" : ""}` : BLANK;
  lines.push(`Recall Cycle: ${cycle}${manual ? "" : `${SEP}Recall exam${withCode(f.recall)}: ${availability(f.recall)}`}`);
  lines.push(`Oral Exam${withCode(f.exam)}: ${freqText(f.exam)}${availabilityCol(f.exam, manual)}`);
  lines.push(`Pan${withCode(f.pan)}: ${freqText(f.pan)}${availabilityCol(f.pan, manual)}`);
  lines.push(`B/W${withCode(f.bitewings)}: ${freqText(f.bitewings)}${availabilityCol(f.bitewings, manual)}`);
  // Not on the paper form; printed only when the carrier was asked about it.
  if (f.fullSeries.row) {
    lines.push(`Full Series${withCode(f.fullSeries)}: ${freqText(f.fullSeries)}${availabilityCol(f.fullSeries, manual)}`);
  }
  lines.push(`Polishing${withCode(f.polishing)}: ${pctAndFreq(f.polishing)}${availabilityCol(f.polishing, manual)}`);
  const units = f.scaling.row && !f.scaling.unavailable ? f.scaling.scalingUnits : BLANK;
  lines.push(
    `Scaling Units: ${units}${SEP}Preventative Scaling${withCode(f.preventativeScaling)}: ${f.preventativeScaling.pct}` +
    `${SEP}Perio Scaling / Root Planing${withCode(f.perioScaling)}: ${pctAndUnits(f.perioScaling)}`,
  );
  const fluoride = f.fluoride.yesNo === "Y" && f.fluoride.pct !== BLANK ? `Y ${f.fluoride.pct}` : f.fluoride.yesNo;
  lines.push(
    `Fluoride${withCode(f.fluoride)}: ${fluoride}${SEP}Age Limit ${orBlank(f.fluoride.ageLimit)}` +
    `${SEP}Sealants${withCode(f.sealants)}: ${f.sealants.yesNo}${f.sealants.ageLimit ? ` to age ${f.sealants.ageLimit}` : ""}` +
    `${SEP}OHI${withCode(f.ohi)}: ${f.ohi.yesNo}`,
  );
  lines.push(`Night Guard${withCode(f.nightGuard)}: ${pctAndFreq(f.nightGuard)}${availabilityCol(f.nightGuard, manual)}`);

  lines.push("");
  lines.push("COVERAGE BY PROCEDURE FAMILY");
  lines.push(
    `Diagnostic: ${f.exam.pct !== BLANK ? f.exam.pct : f.recall.pct}${SEP}Preventative: ${maxima.preventative.words}` +
    `${SEP}Restorative: ${pctLine(f.restorative)}${withCode(f.restorative)}` +
    `${SEP}Composites on Molars${withCode(f.molarComposites)}: ${molarCompositesLine(f.molarComposites)}`,
  );
  lines.push(
    `Endodontics: ${pctLine(f.endodontics)}${basicOrMajor(f.endodontics)}${withCode(f.endodontics)}` +
    `${SEP}Perio Surgery${withCode(f.perioSurgery)}: ${pctLine(f.perioSurgery)}${basicOrMajor(f.perioSurgery)}` +
    `${SEP}Oral Surgery${withCode(f.oralSurgery)}: ${pctLine(f.oralSurgery)}${basicOrMajor(f.oralSurgery)}` +
    (d.perio_endo_in_basic ? `${SEP}Perio & Endo in Basic: ${answerToken(d.perio_endo_in_basic)}` : ""),
  );
  lines.push(
    `Crown${withCode(f.crown)}: ${pctLine(f.crown)}${SEP}Denture${withCode(f.denture)}: ${pctLine(f.denture)}` +
    `${SEP}Reline${withCode(f.reline)}: ${pctLine(f.reline)}${SEP}Bridge${withCode(f.bridge)}: ${pctLine(f.bridge)}`,
  );
  // Lines no paper form asks, printed only when the carrier was asked.
  const asked = [
    ["CBCT", f.cbct], ["Preventive Resin", f.preventiveResin],
    ["Ridge Preservation (PRF)", f.ridgePreservation], ["Implants", f.implants],
  ].filter((pair) => pair[1] && pair[1].row && !pair[1].unavailable)
    .map(([name, fam]) => `${name}${withCode(fam)}: ${fam.status && fam.pct !== "N" ? `${pctLine(fam) !== BLANK ? `${pctLine(fam)}, ` : ""}${fam.status.toLowerCase()}` : pctLine(fam)}`);
  if (asked.length) lines.push(`Also asked: ${asked.join(SEP)}`);
  const orthoOff = f.ortho.pct === "N" || maxima.ortho.off || maxima.ortho.max === "$0.00";
  const orthoMax = maxima.ortho.noMaxStated
    ? `Max ${NO_MAX_STATED}`
    : maxima.ortho.max !== BLANK
      ? (maxima.ortho.lifetime ? `Annual ${BLANK}, LTM ${maxima.ortho.max}` : `Annual ${maxima.ortho.max}, LTM ${BLANK}`)
      : `Annual ${BLANK}, LTM ${BLANK}`;
  // The row's own % first, else the card's (manual entries carry it on the
  // card) — or the card's status when the carrier gave no % ("Predetermination required").
  const orthoPct = f.ortho.pct !== BLANK ? pctLine(f.ortho) : maxima.ortho.words;
  const orthoAge = f.ortho.ageLimit || d.ortho_age_max || null;
  const orthoFrom = d.ortho_age_min ? `, from age ${d.ortho_age_min}` : "";
  const orthoPay = d.ortho_payment && d.ortho_payment !== "Unknown"
    ? `${SEP}Payment: ${d.ortho_payment.toLowerCase()}${d.ortho_initial_payment_pct ? ` (${d.ortho_initial_payment_pct} on the initial fee)` : ""}`
    : "";
  lines.push(orthoOff
    ? `Ortho${withCode(f.ortho)}: N — not covered`
    : `Ortho${withCode(f.ortho)}: ${orthoPct}${SEP}${orthoMax}, Age Limit ${orBlank(orthoAge)}${orthoFrom}${orthoPay}`);

  lines.push("");
  lines.push("EXCEPTIONS TO GENERALLY ACCEPTED RULES");
  if (exceptions.length) {
    exceptions.forEach((e) => lines.push(`- ${e.text}${e.all ? " (all codes)" : e.codes.length ? ` (${e.codes.join(", ")})` : ""}`));
  } else {
    lines.push(`- ${BLANK}`);
  }

  lines.push("");
  lines.push(`NOT AVAILABLE FROM THE CARRIER: ${unavailable.length ? unavailable.join(", ") : "none"}`);
  const copied = dayjs(model.exportedAt || undefined);
  lines.push(
    `Recorded ${orBlank(provenance.date)} via ${provenance.sourceLabel} · Smilepass` +
    `${copied.isValid() ? ` · copied ${copied.format("MMM D, YYYY")}` : ""}`,
  );

  return lines.join("\n");
}
